import React from "react";

import { merge } from "lodash/fp";

import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Card, Modal, Stack, SxProps, Typography } from "@mui/material";

import { ModalCloseButton } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

import { ModalProps } from "./Modal";
import { Row } from "./Stack";

const CONFIRM_BUTTON_STYLE = {
  neutral: {
    default: {},
    inverse: {},
  },
  negative: {
    default: {
      bgcolor: "error.main",
      color: "white",
      "&:hover": {
        bgcolor: "error.dark",
      },
    },
    inverse: {
      color: "error.main",
      "&:hover": {
        bgcolor: "shades.negative.50",
      },
    },
  },
};

const DEFAULT_CONFIRM_PROPS = {
  disabled: false,
  label: "Confirm",
  type: "neutral",
};

const DEFAULT_CANCEL_PROPS = {
  label: "Cancel",
  show: true,
  type: "neutral",
};

type ConfirmationModalSize = "sm" | "md" | "lg" | "xl";

export interface ConfirmationModalProps {
  open: boolean;
  onClose: (event?: Object) => void | NonNullable<ModalProps["onClose"]>;
  onConfirm: (event?: React.MouseEvent) => void;
  cancelProps?: {
    label?: string;
    show?: boolean;
    type?: "neutral" | "negative";
  };
  children?: React.ReactNode;
  confirmProps?: {
    disabled?: boolean;
    label?: string;
    testId?: string;
    type?: "neutral" | "negative";
    tooltip?: string;
  };
  hideButtons?: boolean;
  inverse?: boolean;
  loading?: boolean;
  onCancel?: () => void;
  sx?: SxProps;
  title?: React.ReactNode;
  subtitle?: string;
  size?: ConfirmationModalSize | React.CSSProperties["width"];
  icon?: React.ReactNode;
  modalProps?: Partial<ModalProps>;
  showHeaderCloseButton?: boolean;
}

const WIDTH_BY_SIZE: {
  [key in ConfirmationModalSize]: React.CSSProperties["width"];
} = {
  sm: "400px",
  md: "500px",
  lg: "600px",
  xl: "700px",
};

const ConfirmationModal = ({
  cancelProps = {},
  children,
  confirmProps = {},
  hideButtons,
  inverse = false,
  loading,
  open,
  onClose,
  onCancel,
  onConfirm,
  size = "sm",
  sx,
  title,
  subtitle,
  icon,
  modalProps,
  showHeaderCloseButton = false,
}: ConfirmationModalProps) => {
  const mergedConfirmProps = merge({ ...DEFAULT_CONFIRM_PROPS }, confirmProps);
  const confirmButtonStyleForType =
    CONFIRM_BUTTON_STYLE[mergedConfirmProps.type];

  const mergedCancelProps = merge({ ...DEFAULT_CANCEL_PROPS }, cancelProps);
  const width = WIDTH_BY_SIZE[size as ConfirmationModalSize] ?? size;

  return (
    <Modal open={open} onClose={onClose} disableAutoFocus {...modalProps}>
      <Card
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width,
          maxWidth: "95%",
          maxHeight: "95%",
          p: "24px 24px",
          ...sx,
        }}
      >
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            {title && (
              <Row variant="apart" spacing={1}>
                <Row variant="left" spacing={1}>
                  {icon && icon}
                  <Typography variant="h2" sx={{ color: "shades.neutral.700" }}>
                    {title}
                  </Typography>
                </Row>
                {showHeaderCloseButton && (
                  <ModalCloseButton onClick={onClose} />
                )}
              </Row>
            )}
            {subtitle && (
              <Typography
                variant="bodySecondaryReg"
                sx={{ color: "shades.neutral.500" }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>
          {children}
          {!hideButtons && (
            <Stack
              direction={inverse ? "row-reverse" : "row"}
              justifyContent={inverse ? "flex-start" : "flex-end"}
              sx={{ pt: 1 }}
              spacing={2}
            >
              {mergedCancelProps.show && (
                <Button
                  variant={inverse ? "contained" : "text"}
                  color={
                    mergedCancelProps.type === "negative" ? "error" : undefined
                  }
                  onClick={onCancel ?? onClose}
                  sx={{
                    height: "40px",
                    minWidth: "100px",
                    transition: "border 0.1s ease",
                  }}
                >
                  {mergedCancelProps.label}
                </Button>
              )}
              <TooltipWrapper
                show={mergedConfirmProps.disabled}
                title={mergedConfirmProps.tooltip}
                placement="top"
              >
                <LoadingButton
                  data-test-id={mergedConfirmProps.testId}
                  disabled={mergedConfirmProps.disabled}
                  onClick={onConfirm}
                  loading={loading}
                  variant={inverse ? "text" : "contained"}
                  sx={{
                    height: "40px",
                    minWidth: "100px",
                    ...(inverse
                      ? confirmButtonStyleForType.inverse
                      : confirmButtonStyleForType.default),
                  }}
                >
                  {mergedConfirmProps.label}
                </LoadingButton>
              </TooltipWrapper>
            </Stack>
          )}
        </Stack>
      </Card>
    </Modal>
  );
};

export default ConfirmationModal;
